import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchMorningBriefing } from '@/features/alpha/alpha.service';
import type { MorningBriefingEnvelope } from '@/features/alpha/types/alpha.types';
import { logger } from '@/shared/lib/logger';

/**
 * The Morning Briefing card's data and its "shown once" rule.
 *
 * WHAT THIS HOOK DOES NOT DO: compose, summarise or count anything. It reads
 * the agency's shared briefing from Conv-AI (a cache hit for everyone but the
 * first dispatcher of the day) and remembers, in THIS browser tab only, that the
 * card for that agency + service day was dismissed. The briefing itself is
 * agency data on the backend; nothing about it is stored per user.
 *
 *   dismissed  sessionStorage `alpha-briefing-dismissed` = "{agency}:{service_day}"
 *              — a tab-local convenience, so reopening the app the same day does
 *              not re-nag, and a new service day shows the card again.
 *
 * `enabled` gates the fetch (the chat permission, the feature flag): when false
 * nothing is requested and nothing is shown.
 */
const DISMISSED_KEY = 'alpha-briefing-dismissed';

const readDismissed = (): string | null => {
  try {
    return window.sessionStorage.getItem(DISMISSED_KEY);
  } catch {
    return null;
  }
};

const writeDismissed = (value: string): void => {
  try {
    window.sessionStorage.setItem(DISMISSED_KEY, value);
  } catch {
    /* storage unavailable — the dismissal lives in memory for this render tree */
  }
};

export const briefingIdentity = (briefing: MorningBriefingEnvelope): string =>
  `${briefing.agency}:${briefing.service_day}`;

export interface MorningBriefingState {
  briefing: MorningBriefingEnvelope | null;
  /** True while the first read is in flight. */
  loading: boolean;
  /** The card should be on screen: a briefing exists and this tab has not dismissed it today. */
  visible: boolean;
  dismiss: () => void;
  /** Re-read the shared briefing (a cache hit); `refresh` asks the backend to regenerate for the agency. */
  // eslint-disable-next-line no-unused-vars -- a type signature; the name documents the option
  reload(options?: { refresh?: boolean }): Promise<void>;
}

export const useMorningBriefing = (enabled: boolean, convUiBaseUrl?: string): MorningBriefingState => {
  const [briefing, setBriefing] = useState<MorningBriefingEnvelope | null>(null);
  const [loading, setLoading] = useState(false);
  const [dismissedFor, setDismissedFor] = useState<string | null>(readDismissed);
  const requested = useRef(false);

  const reload = useCallback(
    async ({ refresh = false }: { refresh?: boolean } = {}) => {
      setLoading(true);
      try {
        const response = await fetchMorningBriefing(convUiBaseUrl, { refresh });
        setBriefing(response.briefing ?? null);
      } catch (err) {
        // The card is a convenience: a failed read shows nothing and says nothing.
        logger.debug('[MorningBriefing] briefing unavailable', {
          error: err instanceof Error ? err.message : String(err),
        });
        setBriefing(null);
      } finally {
        setLoading(false);
      }
    },
    [convUiBaseUrl],
  );

  useEffect(() => {
    if (!enabled || requested.current) return;
    requested.current = true;
    void reload();
  }, [enabled, reload]);

  const dismiss = useCallback(() => {
    if (!briefing) return;
    const id = briefingIdentity(briefing);
    writeDismissed(id);
    setDismissedFor(id);
  }, [briefing]);

  const visible = enabled && briefing !== null && dismissedFor !== briefingIdentity(briefing);
  return { briefing, loading, visible, dismiss, reload };
};
